if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')      }
      this.mql = window.matchMedia('(min-width: 750px)');
      this.initGalleryNav();
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

    }

    // The prev/next arrows overlaid on the main image. Below 750px the slides
    // sit side by side and SliderComponent pages between them by scrolling.
    // At 750px+ (thumbnail layouts) only the active slide stays in the flow
    // (the rest are display:none), so scrolling has nothing to reach — those
    // widths are handled here instead, the same way a thumbnail click is.
    //
    // These are the same <button> elements SliderComponent (global.js) binds
    // its own click handler to. At these widths that handler is not a no-op:
    // it still calls scrollTo() with a NaN offset (its item-width math never
    // runs, since display:none slides make it bail out early), which can
    // trigger a spurious scroll -> update() -> a bogus disabled state on
    // these very buttons and a debounced slideChanged dispatch — an
    // intermittent cascade that looked like every-other-click doing nothing.
    // Listening on the wrapper in the capture phase lets us stop the event
    // before it ever reaches the button's own (bubble-phase) listener.
    initGalleryNav() {
      const overlayNav = this.querySelector('.product-gallery-overlay-nav');
      if (!overlayNav) return;
      overlayNav.addEventListener('click', this.onGalleryNavClick.bind(this), true);
    }

    onGalleryNavClick(event) {
      if (!this.dataset.desktopLayout.includes('thumbnail') || !this.mql.matches) return;
      const nextButton = event.target.closest('.slider-button--next');
      const prevButton = event.target.closest('.slider-button--prev');
      if (!nextButton && !prevButton) return;
      event.preventDefault();
      event.stopPropagation();
      const direction = nextButton ? 1 : -1;
      const mediaItems = this.getMediaItems();
      if (mediaItems.length < 2) return;
      const activeIndex = mediaItems.findIndex((item) => item.classList.contains('is-active'));
      const nextIndex = (activeIndex + direction + mediaItems.length) % mediaItems.length;
      this.setActiveMedia(mediaItems[nextIndex].dataset.mediaId, false);
    }

    // Scoped to the <li> slides specifically (not just "[data-media-id]") —
    // the zoom-open button product-thumbnail.liquid renders inside each slide
    // also carries a bare data-media-id (for product-media-modal.liquid, a
    // separate component), so the unscoped selector was matching two elements
    // per slide. That silently doubled this list and made navigation land on
    // that button instead of the slide every other click — nothing has
    // is-active then, so the image "disappears" until the next click
    // happens to land back on a real slide.
    getMediaItems() {
      return Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]'));
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`li[data-media-id="${ mediaId }"]`);
      if (!activeMedia) return;

      this.elements.viewer.querySelectorAll('li[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);
      this.updateGalleryCounter(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    updateGalleryCounter(activeMedia) {
      const counter = this.elements.viewer.querySelector('.product-gallery-counter');
      if (!counter) return;
      const mediaItems = this.getMediaItems();
      const currentIndex = mediaItems.indexOf(activeMedia) + 1;
      if (currentIndex < 1) return;
      const currentEl = counter.querySelector('.slider-counter--current');
      const totalEl = counter.querySelector('.slider-counter--total');
      if (currentEl) currentEl.textContent = String(currentIndex).padStart(2, '0');
      if (totalEl) totalEl.textContent = String(mediaItems.length).padStart(2, '0');
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}














